import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Sparkles } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { workspaceApi } from '@/services/workspace-api';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';

/**
 * First-run screen for an orphaned identity (authenticated, no active membership
 * anywhere). Standalone — deliberately NOT wrapped in ChatLayout, which assumes a
 * workspace context. On success, `switchWorkspace` clears the orphan state and
 * the ProtectedRoute guard bounces the user to /home.
 */
const CreateWorkspacePage: React.FC = () => {
  const { user, switchWorkspace, logout } = useAuth();
  const [name, setName] = useState('');

  const createMutation = useMutation({
    mutationFn: async () => {
      const { workspace } = await workspaceApi.create(name.trim());
      await switchWorkspace(workspace.id);
    },
    onError: () => toast.error('Could not create workspace. Please try again.'),
  });

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md">
        <div className="rounded-2xl bg-card border border-border p-8 shadow-sm">
          <div className="w-12 h-12 rounded-xl bg-primary/15 text-primary flex items-center justify-center mb-5">
            <Sparkles className="w-6 h-6" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">
            {user?.firstName ? `Welcome, ${user.firstName}` : 'Welcome'}
          </h1>
          <p className="text-muted-foreground mt-2">
            You're not part of any workspace yet. Create one to get started — it's your top-level
            account for billing, people, and the businesses inside it.
          </p>

          <form
            className="mt-6 space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (name.trim()) createMutation.mutate();
            }}
          >
            <Input
              label="Workspace name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Northside Holdings"
              autoFocus
            />
            <Button
              type="submit"
              className="w-full"
              isLoading={createMutation.isPending}
              disabled={!name.trim()}
            >
              Create workspace
            </Button>
          </form>
        </div>

        <button
          onClick={logout}
          className="mt-4 w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          Sign out
        </button>
      </div>
    </div>
  );
};

export default CreateWorkspacePage;
